import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { clientApi } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import PageHeader from '../components/PageHeader';
import usePageTitle from '../hooks/usePageTitle';
import { cn } from '@/lib/utils';
import {
  Download,
  Package,
  Clock,
  FileText,
  AlertCircle,
  RefreshCw,
  History,
  Info
} from 'lucide-react';
import { toast } from 'sonner';

/* Tailwind needs every class spelled out, so the channel tones are full strings (no
 * `bg-${tone}-500` templates - the JIT never sees those). Production = success, beta = warning,
 * alpha = danger, matching the status vocabulary of the rest of the app. */
const CHANNEL_CONFIG = {
  production: {
    label: 'Production',
    description: 'Stable releases',
    color: 'success',
    dotClass: 'bg-success',
    retention: 'All versions kept forever',
    badgeClass: 'rounded-full border-success/40 text-success bg-success/10'
  },
  beta: {
    label: 'Beta',
    description: 'Testing new features',
    color: 'warning',
    dotClass: 'bg-warning',
    retention: 'Last 6 months',
    badgeClass: 'rounded-full border-warning/40 text-warning bg-warning/10'
  },
  alpha: {
    label: 'Alpha',
    description: 'Experimental builds',
    color: 'danger',
    dotClass: 'bg-danger',
    retention: 'Last 2 months',
    badgeClass: 'rounded-full border-danger/40 text-danger bg-danger/10'
  }
};

const formatFileSize = (bytes) => {
  if (!bytes || bytes === 0) return 'Unknown';
  const mb = bytes / (1024 * 1024);
  return `${mb.toFixed(1)} MB`;
};

const formatDate = (dateStr) => {
  if (!dateStr) return 'Unknown';
  return new Date(dateStr).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
};

const VersionItem = ({ version, channel, isLatest = false }) => {
  const config = CHANNEL_CONFIG[channel];

  const handleDownload = () => {
    const downloadUrl = clientApi.getHistoricalDownloadUrl(channel, version.version);
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.setAttribute('download', version.file_name || `NecroMancer_${version.version}.exe`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    toast.success('Download started!', {
      description: `Downloading ${version.file_name || version.version}`
    });
  };

  return (
    <div
      className={cn(
        'rounded-xl border p-4 transition-colors',
        isLatest
          ? 'bg-gradient-to-r from-primary/10 to-secondary/50 border-primary/30 hover:border-primary/40'
          : 'bg-secondary/60 border-border hover:border-primary/30'
      )}
      data-testid={`version-item-${channel}-${version.version}`}
    >
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-2 mb-1">
            <span className="font-heading font-semibold text-foreground tnum">v{version.version}</span>
            {isLatest && (
              <Badge className="rounded-full text-xs bg-primary/15 text-primary border-primary/30 hover:bg-primary/15">
                Latest
              </Badge>
            )}
          </div>

          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-muted-foreground mb-2 tnum">
            <span className="flex items-center gap-1">
              <Clock className="h-3 w-3" />
              {formatDate(version.release_date)}
            </span>
            <span className="flex items-center gap-1">
              <FileText className="h-3 w-3" />
              {formatFileSize(version.file_size)}
            </span>
          </div>

          {version.release_notes && (
            <p className="text-sm text-foreground/90 line-clamp-2 text-pretty">{version.release_notes}</p>
          )}
        </div>

        {/* The newest build of the open channel is the one primary action on the page. */}
        <Button
          variant={isLatest ? 'default' : 'outline'}
          size="sm"
          onClick={handleDownload}
          aria-label={`Download ${config.label} v${version.version}`}
          className={cn(
            'tap shrink-0 rounded-full w-full sm:w-auto',
            isLatest ? 'shadow-glow' : 'hover:border-primary/40 hover:text-primary'
          )}
          data-testid={`download-btn-${channel}-${version.version}`}
        >
          <Download className="h-4 w-4 mr-1" />
          Download
        </Button>
      </div>
    </div>
  );
};

const ChannelHistoryPanel = ({ channel, versions, latestVersion }) => {
  const config = CHANNEL_CONFIG[channel];

  if (!versions || versions.length === 0) {
    return (
      <div className="aurora-card aurora-card-dashed p-8 text-center sm:p-10" data-testid={`versions-empty-${channel}`}>
        <span className="mx-auto inline-flex h-14 w-14 items-center justify-center rounded-full bg-primary/10 text-primary ring-1 ring-primary/20">
          <Package className="h-6 w-6" />
        </span>
        <p className="mt-4 font-heading text-base font-semibold text-foreground">No versions available</p>
        <p className="mx-auto mt-1 max-w-sm text-sm text-muted-foreground text-pretty">
          No {config.label.toLowerCase()} releases have been uploaded yet.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {versions.map((version, index) => (
        <VersionItem
          key={version.version}
          version={version}
          channel={channel}
          isLatest={index === 0 && version.version === latestVersion}
        />
      ))}
    </div>
  );
};

/* First-load skeleton: the retention strip, then the releases card with its three tabs. */
const VersionHistorySkeleton = () => (
  <div className="space-y-6" aria-busy="true" aria-label="Loading version history" data-testid="versions-skeleton">
    <div className="aurora-card p-4">
      <div className="flex items-start gap-3">
        <div className="h-9 w-9 shrink-0 animate-pulse rounded-xl bg-secondary/60" />
        <div className="min-w-0 flex-1">
          <div className="h-4 w-32 animate-pulse rounded-xl bg-secondary/60" />
          <div className="mt-3 grid grid-cols-1 gap-4 sm:grid-cols-3">
            {[0, 1, 2].map((i) => <div key={i} className="h-5 w-full animate-pulse rounded-xl bg-secondary/60" />)}
          </div>
        </div>
      </div>
    </div>
    <div className="aurora-card p-4 sm:p-6">
      <div className="h-5 w-36 animate-pulse rounded-xl bg-secondary/60" />
      <div className="mt-2 h-3 w-64 max-w-full animate-pulse rounded-xl bg-secondary/60" />
      <div className="mt-6 h-10 w-full animate-pulse rounded-xl bg-secondary/60" />
      <div className="mt-6 space-y-3">
        {[0, 1, 2].map((i) => <div key={i} className="h-24 w-full animate-pulse rounded-xl bg-secondary/60" />)}
      </div>
      <p className="mt-6 flex items-center justify-center gap-2 text-sm text-muted-foreground">
        <RefreshCw className="h-4 w-4 animate-spin text-primary" />
        Loading version history...
      </p>
    </div>
  </div>
);

export default function VersionHistoryPage() {
  usePageTitle('Versions');

  const { data: historyData, isLoading: historyLoading, error: historyError, refetch: refetchHistory } = useQuery({
    queryKey: ['clientHistory'],
    queryFn: () => clientApi.getHistory(null, 50).then(res => res.data),
    staleTime: 60000,
  });

  const { data: channelsData, isLoading: channelsLoading } = useQuery({
    queryKey: ['clientChannels'],
    queryFn: () => clientApi.getChannels().then(res => res.data),
    staleTime: 60000,
  });

  const isLoading = historyLoading || channelsLoading;

  const header = (
    <PageHeader
      icon={History}
      title="Version History"
      description="Download previous versions of the client application"
      back={{ to: '/dashboard', label: 'Dashboard' }}
    />
  );

  if (isLoading) {
    return (
      <div className="space-y-6" data-testid="version-history-page">
        {header}
        <VersionHistorySkeleton />
      </div>
    );
  }

  if (historyError) {
    return (
      <div className="space-y-6" data-testid="version-history-page">
        {header}
        <div className="aurora-card p-8 text-center sm:p-10" role="alert" data-testid="versions-error">
          <span className="mx-auto inline-flex h-14 w-14 items-center justify-center rounded-full bg-danger/10 text-danger ring-1 ring-danger/20">
            <AlertCircle className="h-6 w-6" />
          </span>
          <p className="mt-4 font-heading text-base font-semibold text-foreground">Failed to load version history</p>
          <p className="mx-auto mt-1 max-w-sm text-sm text-muted-foreground text-pretty">{historyError.message}</p>
          <Button variant="outline" onClick={() => refetchHistory()} className="tap mt-5 rounded-full" data-testid="versions-retry">
            <RefreshCw className="h-4 w-4" /> Try again
          </Button>
        </div>
      </div>
    );
  }

  const channels = historyData?.channels || {};
  const retentionPolicy = historyData?.retention_policy || {};
  const latestVersions = channelsData?.channels || {};

  return (
    <div className="space-y-6" data-testid="version-history-page">
      {/* Header */}
      {header}

      {/* Retention Policy Info */}
      <Card className="aurora-card">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <div className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary ring-1 ring-primary/20">
              <Info className="h-5 w-5" />
            </div>
            <div className="min-w-0 flex-1">
              <h3 className="font-heading text-base font-semibold text-foreground mb-2">Retention Policy</h3>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4 text-sm">
                {Object.entries(CHANNEL_CONFIG).map(([channel, config]) => (
                  <div key={channel} className="flex min-w-0 items-center gap-2">
                    <Badge variant="outline" className={cn('shrink-0', config.badgeClass)}>
                      {config.label}
                    </Badge>
                    <span className="truncate text-muted-foreground">{config.retention}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Version Tabs */}
      <Card className="aurora-card">
        <CardHeader className="p-4 pb-3 sm:p-6 sm:pb-3">
          <div className="flex items-center gap-2">
            <Package className="h-5 w-5 text-primary" />
            <CardTitle className="font-heading text-base font-semibold text-foreground">All Releases</CardTitle>
          </div>
          <CardDescription className="text-muted-foreground text-pretty">
            Browse and download all available versions across release channels
          </CardDescription>
        </CardHeader>
        <CardContent className="p-4 pt-0 sm:p-6 sm:pt-0">
          <Tabs defaultValue="production" className="w-full">
            {/* On phones the three triggers are wider than the viewport, so the list scrolls
             * sideways; from sm up it is the usual equal three-column strip. */}
            <TabsList className="flex h-auto w-full justify-start gap-1 overflow-x-auto no-scrollbar rounded-xl bg-secondary/60 p-1 mb-6 sm:grid sm:grid-cols-3">
              {Object.entries(CHANNEL_CONFIG).map(([channel, config]) => {
                const versionCount = channels[channel]?.length || 0;
                return (
                  <TabsTrigger
                    key={channel}
                    value={channel}
                    className="tap shrink-0 sm:shrink rounded-lg px-3 py-1.5 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:shadow-glow"
                    data-testid={`tab-${channel}`}
                  >
                    <span className={cn('mr-2 w-2 h-2 shrink-0 rounded-full', config.dotClass)} aria-hidden />
                    {config.label}
                    <Badge variant="outline" className="ml-2 rounded-full border-transparent bg-foreground/10 px-1.5 text-[11px] text-inherit tnum">
                      {versionCount}
                    </Badge>
                  </TabsTrigger>
                );
              })}
            </TabsList>

            {Object.entries(CHANNEL_CONFIG).map(([channel, config]) => (
              <TabsContent key={channel} value={channel} className="mt-0">
                <div className="mb-4 p-3 rounded-xl bg-secondary/60 border border-border">
                  <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2">
                    <div className="min-w-0">
                      <h4 className="font-heading text-sm font-semibold text-foreground">{config.label} Channel</h4>
                      <p className="text-sm text-muted-foreground">{config.description}</p>
                    </div>
                    <Badge variant="outline" className={cn('shrink-0', config.badgeClass)}>
                      {config.retention}
                    </Badge>
                  </div>
                </div>

                <ChannelHistoryPanel
                  channel={channel}
                  versions={channels[channel] || []}
                  latestVersion={latestVersions[channel]?.version}
                />
              </TabsContent>
            ))}
          </Tabs>
        </CardContent>
      </Card>
    </div>
  );
}
